import { createApi } from '@reduxjs/toolkit/query/react';
import { ProjectsType } from '@types';
import { keepUnusedDataFor } from '../constants/appActions';
import { clearSelectedProject } from '../slices';
import { baseQueryWithReauth } from '../utils/prepHeaders';

export type args = {};
export type responses = {};

export const projectListTag = { type: 'Projects' as const, id: 'projectsAll' };
export const projectDetailTag = (projectId: number) => ({
  type: 'Projects' as const,
  id: `project-${projectId}`,
});
export const projectMutationTags = (projectId?: number) => [
  projectListTag,
  ...(projectId ? [projectDetailTag(projectId)] : []),
];

export const projectsApi = createApi({
  reducerPath: 'projectsApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Projects'],

  endpoints: (builder) => ({
    // Projects
    postProject: builder.mutation<
      ProjectsType.ProjectPartial,
      ProjectsType.ProjectCreate
    >({
      query: (body) => ({
        url: '/core/projects/',
        method: 'POST',
        body,
        validateStatus: (response, result) =>
          response.status === 201 && !result.isError,
      }),
      invalidatesTags: (result) => projectMutationTags(result?.id),
    }),

    getAllProjects: builder.query<ProjectsType.ProjectPartial[], void>({
      query: () => ({
        url: '/core/projects/',
        method: 'GET',
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      keepUnusedDataFor: 3600,
      providesTags: [projectListTag],
    }),

    getProjectMemberOptions: builder.query<
      ProjectsType.ProjectMemberOption[],
      void
    >({
      query: () => ({
        url: '/core/projects/member-options/',
        method: 'GET',
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      keepUnusedDataFor: 300,
    }),

    getProjectById: builder.query<
      ProjectsType.ProjectPartial,
      { projectId: number }
    >({
      query: ({ projectId }) => ({
        url: `/core/projects/${projectId}/`,
        method: 'GET',
        validateStatus: (response, result) =>
          (response.status === 200 || response.status === 404) &&
          !result.isError,
      }),
      keepUnusedDataFor,
      providesTags: (_result, _error, { projectId }) => [
        projectDetailTag(projectId),
      ],
    }),

    putProjectById: builder.mutation<
      ProjectsType.ProjectPartial,
      {
        projectId: number;
        body: ProjectsType.ProjectCreate;
      }
    >({
      query: ({ projectId, body }) => ({
        url: `/core/projects/${projectId}/`,
        method: 'PUT',
        body,
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      invalidatesTags: (_result, _error, { projectId }) =>
        projectMutationTags(projectId),
    }),

    patchProjectById: builder.mutation<
      ProjectsType.ProjectPartial,
      {
        projectId: number;
        body: ProjectsType.ProjectUpdate;
      }
    >({
      query: ({ projectId, body }) => ({
        url: `/core/projects/${projectId}/`,
        method: 'PATCH',
        body,
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      invalidatesTags: (_result, _error, { projectId }) =>
        projectMutationTags(projectId),
    }),

    deleteProjectById: builder.mutation<
      ProjectsType.ProjectPartial,
      { projectId: number }
    >({
      query: ({ projectId }) => ({
        url: `/core/projects/${projectId}/`,
        method: 'DELETE',
        validateStatus: (response) => response.status === 204,
      }),
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        try {
          // Wait for the query to succeed
          await queryFulfilled;

          // Clear the selected project in your slice
          dispatch(clearSelectedProject());
        } catch {
          // Optional: handle deletion failure
          console.error('Failed to delete project');
        }
      },
      invalidatesTags: (_result, _error, { projectId }) =>
        projectMutationTags(projectId),
    }),

    // Project Sheet
    getAllProjectSheets: builder.query<ProjectsType.ProjectSheet[], void>({
      query: () => ({
        url: '/project-management/project-sheets/',
        method: 'GET',
        validateStatus: (response, result) =>
          (response.status === 200 || response.status === 404) &&
          !result.isError,
      }),
      keepUnusedDataFor,
      providesTags: (result) =>
        result ? [{ type: 'Projects', id: 'projectSheetsAll' }] : [],
    }),

    getProjectSheetById: builder.query<
      ProjectsType.ProjectSheet,
      { projectSheetId: number }
    >({
      query: ({ projectSheetId }) => ({
        url: `/project-management/project-sheets/${projectSheetId}/`,
        method: 'GET',
        validateStatus: (response, result) =>
          (response.status === 200 || response.status === 404) &&
          !result.isError,
      }),
      keepUnusedDataFor,
      providesTags: (_res, _error, arg) =>
        _res
          ? [{ type: 'Projects', id: `projectSheet-${arg.projectSheetId}` }]
          : [],
    }),

    postProjectSheet: builder.mutation<
      ProjectsType.ProjectSheet,
      ProjectsType.ProjectSheetCreate
    >({
      query: (body) => ({
        url: `/project-management/project-sheets/`,
        method: 'POST',
        body,
        validateStatus: (response, result) =>
          response.status === 201 && !result.isError,
      }),
      invalidatesTags: [
        'Projects',
        { type: 'Projects', id: 'projectSheetsAll' },
      ],
    }),

    putProjectSheet: builder.mutation<
      ProjectsType.ProjectSheet,
      { projectSheetId: number; body: Partial<ProjectsType.ProjectSheetCreate> }
    >({
      query: ({ projectSheetId, body }) => ({
        url: `/project-management/project-sheets/${projectSheetId}/`,
        method: 'PUT',
        body,
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      invalidatesTags: (_res, _err, arg) => [
        { type: 'Projects', id: 'projectSheetsAll' },
        { type: 'Projects', id: `projectSheet-${arg.projectSheetId}` },
      ],
    }),

    patchProjectSheet: builder.mutation<
      ProjectsType.ProjectSheet,
      { projectSheetId: number; body: Partial<ProjectsType.ProjectSheetCreate> }
    >({
      query: ({ projectSheetId, body }) => ({
        url: `/project-management/project-sheets/${projectSheetId}/`,
        method: 'PATCH',
        body,
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      invalidatesTags: (_res, _err, arg) => [
        { type: 'Projects', id: 'projectSheetsAll' },
        { type: 'Projects', id: `projectSheet-${arg.projectSheetId}` },
      ],
    }),

    deleteProjectSheet: builder.mutation<void, { projectSheetId: number }>({
      query: ({ projectSheetId }) => ({
        url: `/project-management/project-sheets/${projectSheetId}/`,
        method: 'DELETE',
        validateStatus: (response) => response.status === 204,
      }),
      invalidatesTags: (_res, _err, arg) => [
        { type: 'Projects', id: 'projectSheetsAll' },
        { type: 'Projects', id: `projectSheet-${arg.projectSheetId}` },
      ],
    }),

    // Project sheet tasks
    getProjectSheetTasksBySheetId: builder.query<
      ProjectsType.ProjectSheetTaskSchema[],
      { projectSheetId: number }
    >({
      query: ({ projectSheetId }) => ({
        url: `/project-management/project-sheets/${projectSheetId}/tasks/`,
        method: 'GET',
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      keepUnusedDataFor,
      providesTags: (_res, _err, arg) =>
        _res
          ? [
              {
                type: 'Projects',
                id: `projectSheetTasks-${arg.projectSheetId}`,
              },
            ]
          : [],
    }),
  }),
});

export const {
  // Projects
  usePostProjectMutation,
  useGetAllProjectsQuery,
  useGetProjectMemberOptionsQuery,
  useGetProjectByIdQuery,
  usePutProjectByIdMutation,
  usePatchProjectByIdMutation,
  useDeleteProjectByIdMutation,

  // Project sheets
  useGetAllProjectSheetsQuery,
  useGetProjectSheetByIdQuery,
  usePostProjectSheetMutation,
  usePutProjectSheetMutation,
  usePatchProjectSheetMutation,
  useDeleteProjectSheetMutation,

  // Project sheet tasks
  useGetProjectSheetTasksBySheetIdQuery,
} = projectsApi;

// getProjects: builder.query<any[], void>({
//   query: () => '/projects/',
//   providesTags: (result) =>
//     result
//       ? [
//           ...result.map(({ id }) => ({ type: 'Projects' as const, id })),
//           { type: 'Projects', id: 'LIST' },
//         ]
//       : [{ type: 'Projects', id: 'LIST' }],

export namespace ProjectsApi {
  export const Api = projectsApi;
  export type Args = args;
  export type Responses = responses;
}
